import { useCallback, useEffect, useState } from "react";
import FlowPlayground from "../../components/FlowPlayground/FlowPlayground";
import "./WorkflowDetail.scss";
import WorkflowBasicDetails from "../../components/WorkflowBasicDetails/WorkflowBasicDetails";
import WorkflowFormDetails from "../../components/WorkflowFormDetails/WorkflowFormDetails";
import {
  IWorkFlowBasicInfo,
  WorkFlowContext,
} from "../../context/workflow-context";
import {
  addEdge,
  Connection,
  Edge,
  Node,
  Position,
  useEdgesState,
  useNodesState,
} from "reactflow";
import { WorkFlowStage } from "../../models/Workflow";
import ApiService from "../../services/apiService";
import URLS from "../../urls/Urls";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { userDetails } from "../../redux/user/userSelector";
import { FilterOptionSchema } from "../../components/Filter/filter.data";

const Workflow = () => {
  const [tab, setTab] = useState("overview");
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const navigate = useNavigate();
  const [info, setInfo] = useState<IWorkFlowBasicInfo>({
    name: "",
    owner: { id: -1, name: "" },
    description: "",
    attachments: [],
    initAttachments: [],
    guidelines: "",
    form: { id: -1, name: "" },
    published: false,
  });
  const [nodeDetails, setNodeDetails] = useState<{
    [key: number]: WorkFlowStage;
  }>({});
  const [teamList, setTeamList] = useState<FilterOptionSchema[]>([]);
  const { id } = useParams();
  const [mode, setMode] = useState("");
  const location = useLocation();
  const user = useSelector(userDetails);

  useEffect(() => {
    getTeams();
  }, []);

  const getTeams = async () => {
    try {
      const res = await ApiService().client.get(
        URLS.TEAM.GET_TEAMS(user.company_id || -1),
      );
      if (res.data) {
        setTeamList(res.data.teams);
      }
    } catch (e) {
      console.log("Error: ", e);
    }
  };

  useEffect(() => {
    getWorkFlowDetails();
  }, [id]);

  useEffect(() => {
    if (location.pathname.indexOf("edit") > -1) {
      setMode("edit");
    } else if (location.pathname.indexOf("create") > -1) {
      setMode("create");
    } else {
      setMode("view");
    }
  }, [location]);

  const getWorkFlowDetails = async () => {
    if (id) {
      try {
        const { data } = await ApiService().client.get(
          URLS.WORKFLOW.DETAILS(parseInt(id)),
        );
        if (data && data.workflow) {
          const details: { [key: number]: WorkFlowStage } = {};
          setInfo(data.workflow.info);
          const diff = data.workflow.nodes.filter((node: any) => node.start)[0]
            .position;
          setNodes(
            data.workflow.nodes.map((node: any) => {
              details[node.id] = node.details;
              return {
                id: node.id.toString(),
                sourcePosition: Position.Right,
                targetPosition: Position.Left,
                position: {
                  x: node.position.x - diff.x + 20,
                  y: node.position.y - diff.y + 20,
                },
                type: node.type,
                data: { id: node.id },
              };
            }),
          );
          setEdges(
            data.workflow.edges.map((edge: any) => {
              return {
                id: edge.id,
                source: edge.source,
                sourceHandle: edge.source_handle,
                target: edge.target,
                targetHandle: edge.target_handle,
                type: edge.type,
                data: { label: edge.label },
              };
            }),
          );
          setNodeDetails(details);
        }
      } catch (error) {
        console.error("Error fetching workflow details");
      }
    }
  };

  const addNodes = (node: Node) => {
    setNodes((nds) => nds.concat(node));
  };

  const onConnect = useCallback(
    (params: Edge | Connection) => {
      const source = nodes.filter((node) => node.id === params.source)[0];
      let label = "";
      if (source.type === "decision") {
        label = prompt("Add branch condition") || "";
      }
      setEdges((els) =>
        addEdge(
          {
            ...params,
            type: source.type === "normal" ? "smoothstep" : "custom",
            data: { label },
          },
          els,
        ),
      );
    },
    [setEdges, nodes],
  );

  const updateInfo = (key: string, value: any) => {
    setInfo((old) => {
      return {
        ...old,
        [key]: value,
      };
    });
  };

  const updateNodeDetails = (data: WorkFlowStage) => {
    setNodeDetails((old) => {
      return {
        ...old,
        [data.id]: data,
      };
    });
  };

  const saveWorkflow = async (publish: boolean) => {
    try {
      const formData = new FormData();
      formData.append("basics", JSON.stringify(info));
      if (info.attachments.length > 0) {
        info.attachments.forEach((file) => {
          formData.append("attachments[]", file);
          formData.append("display_names[]", file.name);
        });
      }
      nodes.forEach((node: Node) => {
        formData.append(
          "stages[]",
          JSON.stringify({
            ...nodeDetails[parseInt(node.id)],
            position: node.position,
            uuid: node.id,
            type: node.type,
          }),
        );
      });
      edges.forEach((edge) => {
        console.log("Edge: ", edge);
        formData.append(
          "edges[]",
          JSON.stringify({
            source: edge.source,
            source_handle: edge.sourceHandle,
            target: edge.target,
            target_handle: edge.targetHandle,
            type: edge.type,
            label: edge.data?.label,
          }),
        );
      });
      formData.append("publish", publish.toString());
      if (mode === "edit" && id) {
        const { data } = await ApiService().client.put(
          URLS.WORKFLOW.EDIT(parseInt(id)),
          formData,
        );
        navigate(`/workflows/${id}`);
      } else {
        const { data } = await ApiService().client.post(
          URLS.WORKFLOW.CREATE,
          formData,
        );
        navigate(`/workflows/${data.id}`);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const startEdit = () => {
    navigate(`/workflows/${id}/edit`);
  };

  return (
    <div className="workflow-section full-height overflow-hidden full-width flex-column">
      <div className="upper-bar xetgo-font-tag bold px-16 py-12 lfex-row gap-8">
        <span className="cursor-pointer" onClick={() => navigate("/workflows")}>
          Workflows
        </span>
        {mode === "create" ? (
          <span className="sub-title xetgo-font-tag"> / New</span>
        ) : (
          <span className="sub-title xetgo-font-tag"> / {info.name}</span>
        )}
        {mode === "edit" && (
          <span className="sub-title xetgo-font-tag"> / Edit</span>
        )}
      </div>
      <div className="workflow-create-section p-16 flex-column full-width gap-16 border-box flex-1">
        <div className="flex-row align-items-center justify-content-space-between tab-container">
          <div
            className="flex-row p-12 gap-48"
            style={{ paddingBottom: "0px" }}
          >
            <p
              className={`xetgo-font-tag cursor-pointer ${
                tab === "overview" && "selected-tab"
              } workflow-tab`}
              onClick={() => setTab("overview")}
            >
              Overview
            </p>
            <p
              className={`xetgo-font-tag cursor-pointer ${
                tab === "details" && "selected-tab"
              } workflow-tab`}
              onClick={() => setTab("details")}
            >
              Details
            </p>
          </div>
          <div className="flex-row align-items-center gap-12">
            {mode === "view" && (
              <img
                src="https://d2k6zobmg5lufr.cloudfront.net/assets%2F20250225110757-pen-square.svg"
                alt="edit"
                height={16}
                className="cursor-pointer"
                onClick={startEdit}
              />
            )}
            <p
              className="px-18 py-6 xetgo-font-tag bolder"
              style={{
                background: `${info.published ? "#DEFDE9" : "#FBEFD9"}`,
                color: `${info.published ? "#009B34" : "#EE9403"}`,
                borderTopLeftRadius: "22px",
                borderBottomLeftRadius: "22px",
              }}
            >
              {info.published ? "Published" : "Draft"}
            </p>
          </div>
        </div>
        <WorkFlowContext.Provider
          value={{
            nodes,
            edges,
            teamList,
            info,
            addNodes,
            onConnect,
            updateInfo,
            onNodesChange,
            onEdgesChange,
            nodeDetails,
            updateNodeDetails,
            saveWorkflow,
          }}
        >
          <div className="flex-1 overflow-scroll">
            {tab === "overview" && <FlowPlayground />}
            {tab === "details" && <WorkflowBasicDetails />}
          </div>
        </WorkFlowContext.Provider>
        {mode !== "view" && (
          <div className="flex-row justify-content-flex-end align-items-center gap-8">
            <p
              className="xetgo-font-tag px-12 py-8 cursor-pointer"
              style={{
                color: "#2960EC",
                background: "#ffffff",
                border: "1px solid #2960EC",
                borderRadius: "36px",
              }}
              onClick={() => saveWorkflow(false)}
            >
              Save as Draft
            </p>
            <p
              className="xetgo-font-tag px-12 py-8 cursor-pointer"
              style={{
                color: "#ffffff",
                background: "#2960EC",
                borderRadius: "36px",
                border: "1px solid ##960EC",
              }}
              onClick={() => saveWorkflow(true)}
            >
              Publish
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Workflow;
